import {parse} from 'cookie';
import apiConfig from "app/api/apiConfig.json";
import { getAccessToken } from 'app/auth/getAccessToken';
import { AuthenticationResult, PublicClientApplication } from '@azure/msal-browser';

const API_BASE_URL = `${import.meta.env.VITE_API_URL}/${apiConfig.apiSuffix}`.replace(/\/+$/, "");

type JsonFactory<T> = () => Promise<T>;

const inFlightGetRequests = new Map<string, Promise<any>>();
const responseCache = new Map<string, { timestamp: number; data: any }>();
const RESPONSE_CACHE_TTL_MS = 1500;

class NetworkError extends Error {
    constructor(message = "Network error while contacting API.") {
        super(message);
        this.name = "NetworkError";
    }
}

function getSessionId() {
    const cookies = parse(document.cookie || '');
    return cookies['EcIoSession'] ?? '';
}

function buildSignalRHeaders(auth: AuthenticationResult): Record<string, string> {
    const headers: Record<string, string> = {
        'X-EcIoSession': getSessionId(),
    };

    if (auth.accessToken) {
        const subKey = import.meta.env.VITE_APIM_SUBSCRIPTION_KEY;
        if (subKey) headers['Ocp-Apim-Subscription-Key'] = subKey;
    }

    return headers;
}

/** Auth options for @microsoft/signalr (Bearer via accessTokenFactory; same APIM/session headers as REST). */
export async function getSignalRConnectionAuth(instance: PublicClientApplication): Promise<{
    accessTokenFactory: () => Promise<string>;
    headers: Record<string, string>;
}> {
    const auth = await getToken(instance);
    return {
        accessTokenFactory: async () => (await getToken(instance)).accessToken,
        headers: buildSignalRHeaders(auth),
    };
}

function buildHeaders(auth: AuthenticationResult | undefined, includeContentType = false): Record<string, string> {
    const headers: Record<string, string> = {
        'Accept': 'application/json',
        'X-EcIoSession': getSessionId()
    };

    if (includeContentType) {
        headers['Content-Type'] = 'application/json';
    }

    if (auth?.accessToken) {
        headers['Authorization'] = `Bearer ${auth.accessToken}`;
        const subKey = import.meta.env.VITE_APIM_SUBSCRIPTION_KEY;
        if (subKey) headers['Ocp-Apim-Subscription-Key'] = subKey;
    }

    headers['Cache-Control'] = 'no-cache';
    headers['Pragma'] = 'no-cache';
    return headers;
}

function getDedupedPromise<T>(key: string, factory: JsonFactory<T>): Promise<T> {
    const now = Date.now();
    const cached = responseCache.get(key);
    if (cached && (now - cached.timestamp) <= RESPONSE_CACHE_TTL_MS) {
        return Promise.resolve(cached.data as T);
    }

    const existing = inFlightGetRequests.get(key) as Promise<T> | undefined;
    if (existing) {
        return existing;
    }

    const promise = factory().then(result => {
        responseCache.set(key, { timestamp: Date.now(), data: result });
        return result;
    });

    const wrappedPromise = promise.finally(() => {
        if (inFlightGetRequests.get(key) === wrappedPromise) {
            inFlightGetRequests.delete(key);
        }
    }) as Promise<T>;

    inFlightGetRequests.set(key, wrappedPromise);
    return wrappedPromise;
}

async function getToken(instance: PublicClientApplication): Promise<AuthenticationResult> {
  return getAccessToken(instance);
}

async function safeFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (e: any) {
    if (String(e).includes("Failed to fetch")) {
      throw new NetworkError();
    }
    throw e;
  }
}

export function createLocalApi(instance: PublicClientApplication) {
    const requestJson = (url: string, errorMessage: string): Promise<any> => {
        const fullUrl = `${API_BASE_URL}/${url.replace(/^\/+/, "")}`;
        const key = `GET:${fullUrl}`;
        return getDedupedPromise(key, async () => {
            const token = await getToken(instance);

            const res = await safeFetch(fullUrl, {
                headers: buildHeaders(token),
                cache: 'no-store',
            });

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                const detail = text || res.statusText || errorMessage;
                const err = new Error(`${errorMessage} (HTTP ${res.status}) — ${detail}`.trim());
                (err as any).status = res.status;
                throw err;
            }
        
            return (res.status === 204) ?  null : res.json();
        });
    };

    async function fetchSingle<T>(url: string, parser: (item: any) => T, errorMessage: string) {
        const data = await requestJson(url, errorMessage);
        return parser(data);
    }

    async function fetchList<T>(
        url: string,
        parser: (item: any) => T,
        errorMessage: string,
        getResultArray?: (result: any) => any[]
    ): Promise<T[]> {   
        const data = await requestJson(url, errorMessage);
        const array = getResultArray ? getResultArray(data) : data;
        if (array == null) {
            return [];
        }
        if (!Array.isArray(array)) {
            throw new Error('Unexpected response shape');
        }
        
        return array.map(parser);
    }

    async function call(url: string, verb: string, body: any) {
        const fullUrl = `${API_BASE_URL}/${url.replace(/^\/+/, "")}`;
        const token = await getToken(instance);
        try {
            const response = await safeFetch(fullUrl, {
                method: verb,
                body,
                headers: buildHeaders(token, !!body),
            });
            responseCache.clear();
            return response;
        } catch (err) {
            console.error("[API] call failed:", err);
            throw err;
        }
    }

    return { fetchSingle, fetchList, call };
}